import { StyleSheet, css } from 'aphrodite'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CompanyLogoUpload from '../components/CompanyLogoUpload'
import GooglePlacesSelect from '../components/GooglePlacesSelect'
import MultiSelect, { type Option } from '../components/MultiSelect'
import BrokerInvoiceItemsTable from '../components/invoice/BrokerInvoiceItemsTable'
import { getUserService } from '../services/authService'
import { invoiceService } from '../services/invoiceService'
import { projectService } from '../services/projectService'
import { tagService } from '../services/tagService'
import { userService } from '../services/userService'
import { workOrderService } from '../services/workOrderService'
import type {
  BrokerInvoiceItem,
  CreateInvoiceData,
  InvoiceItem,
} from '../types/invoice'
import { isBrokerInvoiceItem } from '../types/invoice'
import type { Project } from '../types/project'
import { ProjectStatus } from '../types/project'
import type { PayRateTypes, Tag } from '../types/tag'
import type { Contact, User } from '../types/user'
import type { WorkOrder, WorkOrderReportTag } from '../types/workorder'

type GooglePlaceOption = { label: string; value: string }

interface CreateBrokerInvoicePageProps {
  invoiceType?: 'BROKER' | 'CONTRACTOR'
}

const CreateBrokerInvoicePage: React.FC<CreateBrokerInvoicePageProps> = ({
  invoiceType = 'BROKER',
}) => {
  const fetchInvoiceObjects =
    invoiceType === 'CONTRACTOR'
      ? invoiceService.getContractorObjects
      : invoiceService.getBrokerObjects
  const navigate = useNavigate()
  const location = useLocation()
  const [tags, setTags] = useState<Tag[]>([])
  const [brokerTags, setBrokerTags] = useState<WorkOrderReportTag[]>([])
  const [filteredTags, setFilteredTags] = useState<Tag[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(50)
  const [originalInvoiceData, setOriginalInvoiceData] =
    useState<CreateInvoiceData | null>(null)
  const [invoice, setInvoice] = useState<CreateInvoiceData>({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    bill_to_user_id: 0,
    company_name: '',
    company_address: '',
    items: [],
    invoice_type: invoiceType,
  })
  const [contractorNames, setContractorNames] = useState<
    Record<number, string>
  >({})
  const [user, setUser] = useState<User | null>(null)
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<
    number | undefined
  >(undefined)
  const [workorders, setWorkorders] = useState<WorkOrder[]>([])
  const [selectedWorkorderIds, setSelectedWorkorderIds] = useState<number[]>([])
  const [selectedTagCache, setSelectedTagCache] = useState<
    Record<number, { id: number; label: string }>
  >({})
  const [preservedTags, setPreservedTags] = useState<Tag[]>([])
  const [rateTypeByTagId, setRateTypeByTagId] = useState<
    Record<number, string>
  >({})
  const [isLoadingPreloadedTags, setIsLoadingPreloadedTags] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      // Check if we have preloaded tags - if so, show loading state
      const preloadedTags = location.state?.preloadedTags as
        | WorkOrderReportTag[]
        | undefined
      if (preloadedTags && preloadedTags.length > 0) {
        setIsLoadingPreloadedTags(true)
      }
      try {
        // Check if we have preloaded tags from navigation state
        const preloadedTags = location.state?.preloadedTags as
          | WorkOrderReportTag[]
          | undefined

        if (preloadedTags && preloadedTags.length > 0) {
          // Use preloaded tags from TagReportPage
          setBrokerTags(preloadedTags)

          // Use contractor info from tag report data
          const contractorNamesMap: Record<number, string> = {}
          for (const tag of preloadedTags) {
            console.log('Broker tag data for invoice:', {
              id: tag.id,
              contractor: tag.contractor,
              broker: tag.broker,
              destination_address: tag.destination_address,
              origin_address: tag.origin_address,
              material: tag.material,
              pay_rate: tag.pay_rate,
            })

            // Use contractor info from tag report if available
            if (tag.contractor) {
              contractorNamesMap[tag.id] =
                `${tag.contractor.first_name} ${tag.contractor.last_name}`
            } else {
              contractorNamesMap[tag.id] = '-'
            }
          }
          console.log(
            'Contractor names map for broker tags:',
            contractorNamesMap,
          )
          setContractorNames(contractorNamesMap)

          // Pre-populate invoice items with completed tags
          const preloadedItems: BrokerInvoiceItem[] = []
          for (const tag of preloadedTags) {
            try {
              // Use total_hours directly from WorkOrderReportTag (like CreateDriverInvoicePage)
              const totalHours = tag.total_hours || 0
              const loadCount = await tagService.getLoadCount(tag.id.toString())

              // Get project data for rate
              let project: Project | null = null
              // project_id may be a number, string, or object with id
              const rawProjectId = ((): unknown => {
                const v = (tag as { project_id?: unknown }).project_id
                if (v && typeof v === 'object')
                  return (v as { id?: unknown }).id
                return v
              })()
              const numericProjectId = ((): number | null => {
                if (typeof rawProjectId === 'number') return rawProjectId
                if (typeof rawProjectId === 'string') {
                  const n = Number.parseInt(rawProjectId, 10)
                  return Number.isFinite(n) ? n : null
                }
                return null
              })()
              if (numericProjectId != null) {
                try {
                  project = await projectService.getProject(numericProjectId)
                } catch (error) {
                  console.warn(
                    'Failed to fetch project for tag:',
                    tag.id,
                    error,
                  )
                }
              }

              // Get contractor info
              let contractor = null
              if (tag.contractor) {
                try {
                  contractor = await userService.getUserById(tag.contractor.id)
                } catch (error) {
                  console.warn(
                    'Failed to fetch contractor for tag:',
                    tag.id,
                    error,
                  )
                }
              }

              // Fetch workorder for rate_type if available
              let workorder: WorkOrder | null = null
              if (tag.workorder_id) {
                try {
                  workorder = await workOrderService.getWorkOrder(
                    tag.workorder_id,
                  )
                } catch (error) {
                  console.warn(
                    'Failed to fetch workorder for tag:',
                    tag.id,
                    error,
                  )
                }
              }

              // Resolve rate: tag.pay_rate (set from dispatch) > workorder > project
              const computedRate =
                Number(tag.pay_rate) ||
                Number(workorder?.pay_rate) ||
                Number(project?.pay_rate) ||
                0
              const computedHours = totalHours
              // Resolve rate_type: workorder > project
              const resolvedRateType = String(
                workorder?.rate_type || project?.rate_type || '',
              ).toUpperCase()
              const isLoadBased = resolvedRateType === 'LOAD'
              const loadsCount = Number(loadCount) || 0
              const baseTotal = isLoadBased
                ? loadsCount * computedRate
                : computedHours * computedRate
              const computedTotal = Math.max(0, baseTotal)
              setRateTypeByTagId((prev) => ({
                ...prev,
                [tag.id]: String(
                  workorder?.rate_type || project?.rate_type || '',
                ),
              }))

              const brokerItem: BrokerInvoiceItem = {
                id: -Math.floor(Math.random() * 1000000),
                tag_id: tag.id,
                rate: computedRate,
                hours: computedHours,
                job_site:
                  project?.job_site ||
                  tag.origin_name ||
                  tag.destination_address?.split(',')[0] ||
                  '',
                date: tag.start_date
                  ? new Date(tag.start_date).toISOString().split('T')[0]
                  : undefined,
                contractor: contractor?.id,
                number_of_loads: loadCount.toString(),
                material: tag.material || '',
                description: tag.label,
                total: computedTotal.toFixed(2),
              }

              preloadedItems.push(brokerItem)
            } catch (error) {
              console.error(
                'Error processing tag for broker invoice:',
                tag.id,
                error,
              )
            }
          }

          console.log('Preloaded broker invoice items:', preloadedItems)
          setInvoice((prev) => ({
            ...prev,
            items: preloadedItems,
          }))
        } else {
          // Load projects for filter UI
          try {
            const projResp = await projectService.getProjects({
              page_size: 1000,
              status: ProjectStatus.ACCEPTED,
            })
            setProjects(projResp.items)
          } catch (e) {
            console.warn('Failed to load projects for filter UI', e)
          }

          // Fetch tags with optional filters from query string
          const usp = new URLSearchParams(location.search)
          const projectIdParam = usp.get('project_id')
          const workorderIdParam = usp.get('workorder_id')
          const objects = await fetchInvoiceObjects({
            project_id: projectIdParam ? Number(projectIdParam) : undefined,
            workorder_id: workorderIdParam
              ? Number(workorderIdParam)
              : undefined,
          })
          setTags(objects)
          setFilteredTags(objects)
          setSelectedTagCache((prev) => {
            const next = { ...prev }
            for (const t of objects) next[t.id] = { id: t.id, label: t.label }
            return next
          })
          if (projectIdParam) setSelectedProjectId(Number(projectIdParam))
          if (workorderIdParam)
            setSelectedWorkorderIds([Number(workorderIdParam)])
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error)
        toast.error('Failed to fetch tags. Please try again.')
      } finally {
        setIsLoadingPreloadedTags(false)
      }
    }
    fetchData()
  }, [location.state?.preloadedTags, location.search, fetchInvoiceObjects])

  // When project changes, load its workorders and refetch invoice objects
  useEffect(() => {
    const run = async () => {
      if (!brokerTags.length) {
        try {
          if (selectedProjectId) {
            // Load workorders for this project
            const resp = await workOrderService.getWorkOrders({
              projectId: selectedProjectId,
              page_size: 1000,
            })
            setWorkorders(resp.items)
          } else {
            setWorkorders([])
          }
          // Fetch filtered tags from backend
          const objects = await fetchInvoiceObjects({
            project_id: selectedProjectId,
            workorder_id:
              selectedWorkorderIds.length === 1
                ? selectedWorkorderIds[0]
                : undefined,
          })
          // If multiple workorders are selected, filter client-side
          const filtered =
            selectedWorkorderIds.length > 1
              ? objects.filter((t) => {
                  const wid =
                    typeof t.workorder === 'object'
                      ? t.workorder?.id
                      : (t.workorder as number)
                  return selectedWorkorderIds.includes(Number(wid))
                })
              : objects
          setTags(filtered)
          setFilteredTags(filtered)
          setSelectedTagCache((prev) => {
            const next = { ...prev }
            for (const t of filtered) next[t.id] = { id: t.id, label: t.label }
            return next
          })
          setCurrentPage(1)
        } catch (e) {
          console.error('Failed to apply filters:', e)
        }
      }
    }
    run()
  }, [
    selectedProjectId,
    selectedWorkorderIds,
    brokerTags.length,
    fetchInvoiceObjects,
  ])

  // No search bar; keep filteredTags equal to tags
  useEffect(() => {
    setFilteredTags(tags)
    setCurrentPage(1)
  }, [tags])

  // Preserve already-selected tags in dropdown even when filters/pagination change
  useEffect(() => {
    const ensureSelectedTagsPresent = async () => {
      try {
        const currentIds = new Set(tags.map((t) => t.id))
        const filteredIds = new Set(filteredTags.map((t) => t.id))
        const preservedIds = new Set(preservedTags.map((t) => t.id))
        const selectedIds = Array.from(
          new Set(
            invoice.items
              .filter(isBrokerInvoiceItem)
              .map((it) => it.tag_id)
              .filter((id): id is number => typeof id === 'number' && id > 0),
          ),
        )
        const neededIds = selectedIds.filter(
          (id) =>
            !currentIds.has(id) &&
            !filteredIds.has(id) &&
            !preservedIds.has(id),
        )
        if (neededIds.length === 0) return

        const fetched = await Promise.all(
          neededIds.map(async (id) => {
            try {
              return await tagService.getTag(String(id))
            } catch (e) {
              console.warn('Failed to fetch missing selected tag', id, e)
              return null
            }
          }),
        )
        const valid = fetched.filter((t): t is Tag => Boolean(t))
        if (valid.length > 0) {
          setPreservedTags((prev) => {
            const byId = new Map(prev.map((t) => [t.id, t] as const))
            for (const t of valid) byId.set(t.id, t)
            return Array.from(byId.values())
          })
        }
      } catch (e) {
        console.warn('Error preserving selected tags (broker)', e)
      }
    }
    ensureSelectedTagsPresent()
  }, [invoice.items, tags, filteredTags, preservedTags])

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getUserService()
        if (!response.error && response.data?.user) {
          setUser(response.data.user)
          setCurrentLogoUrl(response.data.user.logo_url || null)

          // Fetch and auto-populate company information
          try {
            const company = await userService.getUserCompany()
            if (company) {
              setInvoice((prev) => ({
                ...prev,
                company_name: company.name || '',
                company_address: company.address || '',
              }))
            }
          } catch (error) {
            console.error('Failed to fetch company information:', error)
            // Continue without company info - not critical
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error)
      }
    }
    fetchUser()
  }, [])

  // Load contacts for Bill To selector
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const contactsList = await userService.getContacts()
        setContacts(contactsList)
      } catch (e) {
        console.error('Failed to load contacts', e)
      }
    }
    loadContacts()
  }, [])

  const addItem = () => {
    const newItem: BrokerInvoiceItem = {
      id: -Math.floor(Math.random() * 1000000),
      tag_id: -1,
      rate: 0,
      hours: undefined,
      job_site: undefined,
      date: undefined,
      contractor: undefined,
      number_of_loads: undefined,
      material: undefined,
      total: '0.00',
    }
    setInvoice((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }))
  }

  const updateItem = (
    index: number,
    field: string,
    value: string | number | undefined,
  ) => {
    let newValue: string | number | undefined = value
    if (['hours', 'rate'].includes(field)) {
      newValue = value === '' ? undefined : Number(value)
    }
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          return { ...item, [field]: newValue } as BrokerInvoiceItem
        }
        return item
      }),
    }))
  }

  const handleObjectSelect = async (index: number, objectId: number) => {
    if (objectId === -1) return
    const selectedTag = tags.find(
      (tag) => tag.id?.toString() === objectId.toString(),
    )
    if (selectedTag) {
      try {
        const rawProjectId =
          typeof selectedTag.project === 'object'
            ? (selectedTag.project as { id?: unknown })?.id
            : selectedTag.project
        let project: Project | null = null
        const numericProjectId = ((): number | null => {
          if (typeof rawProjectId === 'number') return rawProjectId
          if (typeof rawProjectId === 'string') {
            const n = Number.parseInt(rawProjectId, 10)
            return Number.isFinite(n) ? n : null
          }
          return null
        })()
        if (numericProjectId != null) {
          try {
            project = await projectService.getProject(numericProjectId)
          } catch (error) {
            console.error('Failed to fetch project:', error)
            // Continue without project data
          }
        }
        const contractor = project?.created_by || null

        // Calculate hours and loads using the same service methods as driver invoice
        const totalHours = await tagService.calculateTotalTime(
          selectedTag.id.toString(),
          selectedTag,
        )
        const loadCount = await tagService.getLoadCount(
          selectedTag.id.toString(),
        )

        // Resolve pay_rate and rate_type from workorder (dispatch), project, or tag
        const wo =
          typeof selectedTag.workorder === 'object'
            ? selectedTag.workorder
            : null
        const resolvedRate =
          Number(wo?.pay_rate) ||
          Number(project?.pay_rate) ||
          Number(selectedTag.pay_rate) ||
          0
        const resolvedRateType = String(
          wo?.rate_type || project?.rate_type || '',
        ).toUpperCase()

        setInvoice((prev) => {
          const newItems = prev.items.map((item, i) => {
            if (i === index) {
              const updatedItem = {
                ...item,
                tag_id: objectId,
                material: selectedTag.material || '',
                contractor: contractor?.id,
                rate: resolvedRate,
                job_site:
                  project?.job_site ||
                  selectedTag.origin_name ||
                  selectedTag.destination_address?.split(',')[0] ||
                  '',
                date: selectedTag.start_date
                  ? new Date(selectedTag.start_date).toISOString().split('T')[0]
                  : undefined,
                hours: totalHours,
                number_of_loads: loadCount.toString(),
                description: selectedTag.label,
              } as BrokerInvoiceItem

              // Compute total based on rate type
              const isLoadBased = resolvedRateType === 'LOAD'
              const rate = Number(updatedItem.rate || 0)
              const loads = Number(loadCount) || 0
              const hours = Number(totalHours) || 0
              const baseTotal = isLoadBased ? loads * rate : hours * rate
              return {
                ...updatedItem,
                total: Math.max(0, baseTotal).toFixed(2),
              }
            }
            return item
          })
          return {
            ...prev,
            items: newItems,
          }
        })
        setRateTypeByTagId((prev) => ({
          ...prev,
          [objectId]: String(wo?.rate_type || project?.rate_type || ''),
        }))
        // Cache the selected tag option for persistence in dropdown
        setSelectedTagCache((prev) => ({
          ...prev,
          [objectId]: { id: objectId, label: selectedTag.label },
        }))
        // Update contractor names map
        if (contractor) {
          setContractorNames((prev) => ({
            ...prev,
            [selectedTag.id]:
              `${contractor.first_name} ${contractor.last_name}`.trim(),
          }))
        }
      } catch (error) {
        console.error('Error fetching tag data:', error)
        toast.error('Failed to fetch tag data')
      }
    }
  }

  const handleSubmit = async () => {
    // Validate that a logo is uploaded (check both currentLogoUrl and user.logo_url)
    const logoUrl = currentLogoUrl || user?.logo_url
    if (!logoUrl) {
      toast.error('Please upload a company logo before creating the invoice.')
      return
    }

    try {
      setIsSubmitting(true)
      const invoiceData: CreateInvoiceData = {
        ...invoice,
        invoice_type: invoiceType,
        items: invoice.items.filter(isBrokerInvoiceItem).map((item) => ({
          ...item,
          tag_id: item.tag_id,
          total: (() => {
            const val =
              typeof item.total === 'string'
                ? item.total
                : String(item.total ?? '0')
            const num = Number.parseFloat(val)
            return (Number.isFinite(num) && num >= 0 ? num : 0).toFixed(2)
          })(),
        })),
      }
      const response = await invoiceService.create(invoiceData)
      setOriginalInvoiceData(invoiceData)
      toast.success('Invoice created successfully!')

      // Redirect to the newly created invoice view page
      navigate(`/invoices/view/${response.id}`)
    } catch (error) {
      console.error('Failed to create invoice:', error)
      toast.error('Failed to create invoice. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLogoUpdate = (logoUrl: string | null) => {
    setCurrentLogoUrl(logoUrl)
  }

  if (isLoadingPreloadedTags) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.loadingContainer)}>
          <i className="fa-solid fa-spinner fa-spin fa-2x" />
          <span className={css(styles.loadingText)}>
            Loading invoice data...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.invoiceContainer)}>
        <div className={css(styles.header)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CompanyLogoUpload
              currentLogoUrl={currentLogoUrl}
              onLogoUpdate={handleLogoUpdate}
              size="medium"
              showRemoveButton={false}
            />
          </div>
          <h1 className={css(styles.invoiceTitle)}>INVOICE</h1>
        </div>

        <div className={css(styles.billingSection)}>
          <div className={css(styles.leftSection)}>
            <div className={css(styles.companySection)}>
              <div className={css(styles.companyHeader)}>Your Company:</div>
              <input
                type="text"
                value={invoice.company_name}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    company_name: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="Your Company"
              />
              <GooglePlacesSelect
                value={
                  invoice.company_address
                    ? {
                        label: invoice.company_address,
                        value: invoice.company_address,
                      }
                    : null
                }
                onChange={(option) =>
                  setInvoice((prev) => ({
                    ...prev,
                    company_address: option?.value || '',
                  }))
                }
                placeholder="Company's Address"
              />
            </div>

            <div className={css(styles.billTo)}>
              <div className={css(styles.billToHeader)}>Bill To</div>
              <MultiSelect
                isMulti={false}
                placeholder="Select contact"
                options={(() => {
                  // Filter contacts that have either user_id or id, and are not the current user
                  const filteredContacts = contacts.filter((c) => {
                    const userId =
                      c.contact_object?.user_id || c.contact_object?.id
                    return userId && userId !== user?.id
                  })
                  const options = filteredContacts.map((c) => {
                    const userId =
                      c.contact_object.user_id || c.contact_object.id
                    // These fields should be available from the backend after the update
                    const companyName =
                      c.contact_object.company_name || 'No Company'
                    const userType = c.contact_object.user_type || 'Unknown'
                    const displayName =
                      c.contact_object.name || c.contact_object.email

                    return {
                      value: String(userId),
                      label: displayName, // Just the name for the main label
                      name: displayName,
                      email: c.contact_object.email,
                      companyName: companyName,
                      userType: userType,
                    } as Option
                  })
                  return options
                })()}
                value={
                  invoice.bill_to_user_id
                    ? (() => {
                        const selectedContact = contacts.find(
                          (c) =>
                            (c.contact_object.user_id ||
                              c.contact_object.id) === invoice.bill_to_user_id,
                        )
                        if (selectedContact) {
                          const companyName =
                            selectedContact.contact_object.company_name ||
                            'No Company'
                          const userType =
                            selectedContact.contact_object.user_type ||
                            'Unknown'
                          const displayName =
                            selectedContact.contact_object.name ||
                            selectedContact.contact_object.email
                          return {
                            value: String(invoice.bill_to_user_id),
                            label: displayName,
                            name: displayName,
                            email: selectedContact.contact_object.email,
                            companyName: companyName,
                            userType: userType,
                          } as Option
                        }
                        return undefined
                      })()
                    : undefined
                }
                onChange={(opt: Option | Option[] | null) => {
                  const single = !Array.isArray(opt) ? opt : opt ? opt[0] : null
                  setInvoice((prev) => ({
                    ...prev,
                    bill_to_user_id: single ? Number.parseInt(single.value) : 0,
                  }))
                }}
              />
            </div>
          </div>

          <div className={css(styles.invoiceInfo)}>
            <div className={css(styles.invoiceInfoHeader)}>Invoice Details</div>
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>Invoice Date</span>
              <input
                type="date"
                value={invoice.invoice_date}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    invoice_date: e.target.value,
                  }))
                }
                className={css(styles.input)}
              />
            </div>
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>Due Date</span>
              <input
                type="date"
                value={invoice.due_date}
                onChange={(e) =>
                  setInvoice((prev) => ({ ...prev, due_date: e.target.value }))
                }
                className={css(styles.input)}
              />
            </div>
          </div>
        </div>

        {/* Additional Invoice Details */}
        <div className={css(styles.additionalFieldsSection)}>
          <div className={css(styles.fieldRow)}>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>Summary Description</label>
              <textarea
                value={invoice.summary_description || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    summary_description: e.target.value,
                  }))
                }
                className={css(styles.textarea)}
                placeholder="Brief description of work performed"
                rows={3}
              />
            </div>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>How to Pay</label>
              <textarea
                value={invoice.how_to_pay || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    how_to_pay: e.target.value,
                  }))
                }
                className={css(styles.textarea)}
                placeholder="Payment instructions (e.g., bank details, check address)"
                rows={3}
              />
            </div>
          </div>
          <div className={css(styles.fieldRow)}>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>PO Number</label>
              <input
                type="text"
                value={invoice.purchase_order_number || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    purchase_order_number: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="Purchase order number"
              />
            </div>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>Payment Terms</label>
              <input
                type="text"
                value={invoice.payment_terms_text || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    payment_terms_text: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="e.g., Net 30, Due on receipt"
              />
            </div>
          </div>
        </div>

        <div className={css(styles.itemsTable)}>
          {/* Filter UI */}
          {brokerTags.length === 0 && (
            <div className={css(styles.filterBar)}>
              <div className={css(styles.filterGroup)}>
                <label className={css(styles.filterLabel)}>Project</label>
                <MultiSelect
                  isMulti={false}
                  placeholder="All projects"
                  options={projects.map((p) => ({
                    value: String(p.id),
                    label: `${p.label}${p.job_site ? ` — ${p.job_site}` : p.job_id ? ` — ${p.job_id}` : ''}`,
                  }))}
                  value={
                    selectedProjectId
                      ? ({
                          value: String(selectedProjectId),
                          label: (() => {
                            const p = projects.find(
                              (pp) => pp.id === selectedProjectId,
                            )
                            return p
                              ? `${p.label}${p.job_site ? ` — ${p.job_site}` : p.job_id ? ` — ${p.job_id}` : ''}`
                              : ''
                          })(),
                        } as Option)
                      : null
                  }
                  onChange={(opt: Option | Option[] | null) => {
                    const single = !Array.isArray(opt)
                      ? opt
                      : opt
                        ? opt[0]
                        : null
                    const val = single ? Number(single.value) : undefined
                    setSelectedProjectId(val)
                    setSelectedWorkorderIds([])
                  }}
                />
              </div>
              <div className={css(styles.filterGroup)}>
                <label className={css(styles.filterLabel)}>Dispatches</label>
                <MultiSelect
                  isMulti
                  placeholder={
                    selectedProjectId
                      ? 'Filter dispatches'
                      : 'Select a project first'
                  }
                  options={workorders.map((wo) => ({
                    value: String(wo.id),
                    label: `${wo.label || `Dispatch ${wo.id}`}${wo.material ? ` — ${wo.material}` : ''}`,
                  }))}
                  value={
                    selectedWorkorderIds.map((id) => {
                      const w = workorders.find((w) => Number(w.id) === id)
                      return {
                        value: String(id),
                        label: w
                          ? `${w.label || `Dispatch ${id}`}${w.material ? ` — ${w.material}` : ''}`
                          : `Dispatch ${id}`,
                      }
                    }) as unknown as Option[]
                  }
                  onChange={(opt: Option | Option[] | null) => {
                    const arr = Array.isArray(opt) ? opt : opt ? [opt] : []
                    const ids = arr.map((o) => Number(o.value))
                    setSelectedWorkorderIds(ids)
                  }}
                  isDisabled={!selectedProjectId}
                />
              </div>
              {(selectedProjectId || selectedWorkorderIds.length > 0) && (
                <button
                  className={css(styles.clearFiltersButton)}
                  title="Clear filters"
                  onClick={() => {
                    setSelectedProjectId(undefined)
                    setSelectedWorkorderIds([])
                    setWorkorders([])
                  }}
                >
                  <i className="fa-solid fa-eraser" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          )}
          <div className={css(styles.paginationInfo)}>
            {brokerTags.length > 0
              ? `Showing ${brokerTags.length} preloaded tags`
              : `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, filteredTags.length)}-${Math.min(currentPage * itemsPerPage, filteredTags.length)} of ${filteredTags.length} tags`}
          </div>
          {(() => {
            const pageTags =
              brokerTags.length > 0
                ? (brokerTags as (Tag | WorkOrderReportTag)[])
                : (filteredTags.slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  ) as (Tag | WorkOrderReportTag)[])
            const combinedTags = [
              ...pageTags,
              ...preservedTags.filter(
                (pt) => !pageTags.some((t) => t.id === pt.id),
              ),
              ...Object.values(selectedTagCache)
                .filter((ct) => !pageTags.some((t) => t.id === ct.id))
                .map(
                  (ct) => ({ id: ct.id, label: ct.label }) as unknown as Tag,
                ),
            ]
            const selectedIds = (
              invoice.items.filter(isBrokerInvoiceItem) as BrokerInvoiceItem[]
            )
              .map((i) => i.tag_id)
              .filter((id): id is number => typeof id === 'number' && id > 0)
            return (
              <BrokerInvoiceItemsTable
                items={
                  invoice.items.filter(
                    isBrokerInvoiceItem,
                  ) as BrokerInvoiceItem[]
                }
                isEditable={true}
                onItemUpdate={updateItem}
                onItemRemove={(index) =>
                  setInvoice((prev) => ({
                    ...prev,
                    items: prev.items.filter((_, i) => i !== index),
                  }))
                }
                onAddItem={addItem}
                tags={combinedTags}
                tagsFiltered={pageTags}
                contractorNames={contractorNames}
                onObjectSelect={handleObjectSelect}
                currentPage={currentPage}
                totalPages={
                  brokerTags.length > 0
                    ? 1
                    : Math.ceil(filteredTags.length / itemsPerPage)
                }
                onPageChange={setCurrentPage}
                selectedTagIds={selectedIds}
              />
            )
          })()}
        </div>

        <div className={css(styles.actions)}>
          <button
            className={css(styles.submitButton)}
            onClick={handleSubmit}
            disabled={!(currentLogoUrl || user?.logo_url) || isSubmitting}
            title={
              !(currentLogoUrl || user?.logo_url)
                ? 'Please upload a company logo first'
                : ''
            }
          >
            Create Invoice
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'visible',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
    color: '#1C64F2',
  },
  loadingText: {
    fontSize: '16px',
    color: '#374151',
    fontWeight: 500,
  },
  invoiceContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxWidth: '90%',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
  },

  invoiceTitle: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    padding: '16px',
  },
  billingSection: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: '40px',
    marginBottom: '40px',
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  companySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  billTo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    position: 'relative',
    zIndex: 2,
    overflow: 'visible',
  },
  billToHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  invoiceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#F9FAFB',
    padding: '24px',
    borderRadius: '8px',
    height: 'fit-content',
  },
  invoiceInfoHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    width: '100px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  itemsTable: {
    marginBottom: '24px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '24px',
  },
  submitButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#93C5FD',
      cursor: 'not-allowed',
    },
  },

  companyHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  searchSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '16px',
  },
  searchInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  paginationInfo: {
    fontSize: '12px',
    color: '#6B7280',
    whiteSpace: 'nowrap',
  },
  filterBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 260,
  },
  filterLabel: {
    fontSize: '12px',
    color: '#374151',
    fontWeight: 600,
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#fff',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  clearFiltersButton: {
    fontSize: '12px',
    height: 38,
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    color: '#6B7280',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition:
      'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      borderColor: '#D1D5DB',
      color: '#374151',
    },
    ':focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.15)',
      borderColor: '#93C5FD',
    },
  },
  additionalFieldsSection: {
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
})

export default CreateBrokerInvoicePage
